import { lazy, ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { APIURL } from "../../../config/api";
import { getCalendarConfig } from "../../../config/calendarConfigs";
import { toE164PhoneFormatter } from "../../../helpers/AssetHelpers/AssetHelpers";
import { unflattenData } from "../../../helpers/SurveyJSHelpers";
import type { SurveyJSFormRef } from "../../../modules/surveyjs_forms/SurveyJSForm";
import { lv2Theme } from "../../../modules/surveyjs_forms/surveyTheme";
import { useStickyBottomBar } from "../../journey/useStickyBottomBar";
import { AsFeaturedOn } from "./shared/AsFeaturedOn";

export interface CalendlyBookingDetails {
  title?: string;
  fullName: string;
  firstName?: string;
  lastName?: string;
  email: string;
  phone: string;
}

export interface CalendlyCustomQuestionAnswer {
  question: string;
  answer: string;
  position?: number;
}

export interface CalendlyCustomQuestionConfig {
  question: string;
  position?: number;
  required?: boolean;
  label?: string;
  inputType?: "text" | "tel" | "email";
  autoComplete?: string;
  bookingDetailField?: keyof CalendlyBookingDetails;
  getDefaultAnswer?: (details: CalendlyBookingDetails) => string;
}

// Loaded only when a details form is used: SurveyJSForm brings in the portal context (and Firebase) with it.
const SurveyJSForm = lazy(() =>
  import("../../../modules/surveyjs_forms/SurveyJSForm").then((module) => ({ default: module.SurveyJSForm }))
);

/** A SurveyJS form shown in place of the built-in contact fields; its answers fill the booking. */
export interface CalendlyDetailsForm {
  surveyFormId: string;
  schemaPage: { name: string; title?: string; description?: string; elements: any[] };
  /** Form field holding each booking detail; defaults to the lander field names. */
  fieldMap?: Partial<Record<keyof Omit<CalendlyBookingDetails, "fullName">, string>>;
  /** Fires on the visitor's first input in the form (funnel "started" events). */
  onFirstInteraction?: () => void;
}

const defaultDetailsFormFieldMap: Record<keyof Omit<CalendlyBookingDetails, "fullName">, string> = {
  title: "title",
  firstName: "firstName",
  lastName: "lastName",
  email: "email",
  phone: "primaryPhone",
};

const contactFieldErrorKeys = ["firstName", "lastName", "phone", "email"];

interface AppointmentSlot {
  startTime: string;
  endTime?: string;
  label: string;
}

interface AppointmentAvailabilityDay {
  date: string;
  day: number;
  weekday: string;
  slots: AppointmentSlot[];
}

interface AppointmentAvailabilityResponse {
  appointmentType: string;
  label: string;
  durationMinutes: number;
  timezone: string;
  days: AppointmentAvailabilityDay[];
}

export interface AppointmentBookedDetails {
  hostName?: string;
  hostEmail?: string;
  startTime?: string;
  endTime?: string;
  timezone?: string;
  eventName?: string;
}

type AppointmentCalendarBlank = {
  key: string;
  isBlank: true;
};

type AppointmentCalendarDate = {
  key: string;
  isBlank?: false;
  date: string;
  day: number;
};

type AppointmentCalendarCell = AppointmentCalendarBlank | AppointmentCalendarDate;

interface CalendlyCustomWidgetProps {
  appointmentType: string;
  bookingDetails: Partial<CalendlyBookingDetails>;
  onBookingDetailsChange: (details: CalendlyBookingDetails) => void;
  onBooked: (payload: any) => void;
  onBackToQuotes?: () => void;
  summaryEyebrow?: string;
  summaryCopy?: string;
  phoneFallback?: ReactNode;
  customQuestionConfigs?: CalendlyCustomQuestionConfig[];
  enableSmsReminderOptIn?: boolean;
  className?: string;
  onBookedStateChange?: (isBooked: boolean) => void;
  /** `formData` is the details form's answers when `detailsForm` is set. */
  onPostBookingCrmUpdate?: (
    payload: AppointmentBookingPayload,
    details: CalendlyBookingDetails,
    formData?: Record<string, any>
  ) => Promise<void>;
  confirmationCtaLabel?: string;
  confirmationCtaHref?: string;
  onConfirmationCtaClick?: () => void;
  confirmationTitle?: string;
  showAsFeaturedOn?: boolean;
  detailsForm?: CalendlyDetailsForm;
  /** Pins the confirm button to the bottom of the screen while it is below the fold (useStickyBottomBar). */
  stickyConfirmButton?: boolean;
  /** Disables the details inputs and the calendar while the booking is being confirmed. */
  lockInputsWhileBooking?: boolean;
}

interface StoredAppointmentBooking {
  appointmentType: string;
  bookingId?: string;
  bookingDetails?: AppointmentBookedDetails;
  inviteeEmail?: string;
  savedAt: string;
}

interface RawStoredAppointmentBooking {
  status?: string;
  appointmentType?: string;
  eventUri?: string;
  inviteeUri?: string;
  bookingId?: string;
  bookingDetails?: AppointmentBookedDetails;
}

export interface AppointmentBookingPayload {
  status?: string;
  appointmentType?: string;
  eventUri?: string;
  inviteeUri?: string;
  cancelUrl?: string;
  rescheduleUrl?: string;
  calendlyEventJson?: string;
  bookingDetails?: AppointmentBookedDetails;
}

export interface CalendlyBookingConfirmationProps {
  appointmentType?: string;
  bookingDetails?: AppointmentBookedDetails;
  inviteeEmail?: string;
  ctaLabel?: string;
  ctaHref?: string;
  onCtaClick?: () => void;
  title?: string;
  warning?: string;
  showAsFeaturedOn?: boolean;
}

function getAppointmentMonth(date = new Date()) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function shiftAppointmentMonth(month: string, delta: number) {
  const [year, monthNumber] = month.split("-").map(Number);
  const date = new Date(year, monthNumber - 1 + delta, 1);
  return getAppointmentMonth(date);
}

function formatAppointmentMonth(month: string) {
  const [year, monthNumber] = month.split("-").map(Number);
  return new Intl.DateTimeFormat("en-GB", { month: "long", year: "numeric" }).format(new Date(year, monthNumber - 1, 1));
}

function getAppointmentBookingStorageKey(appointmentType: string) {
  return `appointment-booking:${appointmentType}`;
}

function normalizeStoredAppointmentBooking(
  parsed: (StoredAppointmentBooking & RawStoredAppointmentBooking) | null | undefined,
  appointmentType: string
): StoredAppointmentBooking | null {
  if (parsed?.appointmentType !== appointmentType) return null;

  if (parsed.bookingDetails) {
    return {
      appointmentType,
      bookingId: parsed.bookingId ?? parsed.eventUri ?? parsed.inviteeUri,
      bookingDetails: parsed.bookingDetails,
      inviteeEmail: parsed.inviteeEmail,
      savedAt: parsed.savedAt ?? new Date().toISOString(),
    };
  }

  return null;
}

function readStoredAppointmentBooking(appointmentType: string): StoredAppointmentBooking | null {
  try {
    const raw = sessionStorage.getItem(getAppointmentBookingStorageKey(appointmentType));
    if (!raw) return null;
    return normalizeStoredAppointmentBooking(JSON.parse(raw), appointmentType);
  } catch {
    return null;
  }
}

function writeStoredAppointmentBooking(booking: StoredAppointmentBooking) {
  try {
    sessionStorage.setItem(getAppointmentBookingStorageKey(booking.appointmentType), JSON.stringify(booking));
  } catch {
    // Ignore storage failures; booking state still exists in component/backend response.
  }
}

function getAppointmentCalendarDays(month: string): AppointmentCalendarCell[] {
  const [year, monthNumber] = month.split("-").map(Number);
  const first = new Date(year, monthNumber - 1, 1);
  const dayCount = new Date(year, monthNumber, 0).getDate();
  const firstWeekday = (first.getDay() + 6) % 7;
  const blanks: AppointmentCalendarBlank[] = Array.from({ length: firstWeekday }, (_, index) => ({ key: `blank-${index}`, isBlank: true }));
  const days: AppointmentCalendarDate[] = Array.from({ length: dayCount }, (_, index) => {
    const day = index + 1;
    return {
      key: `${month}-${String(day).padStart(2, "0")}`,
      date: `${month}-${String(day).padStart(2, "0")}`,
      day,
    };
  });

  return [...blanks, ...days];
}

function formatSelectedAppointmentDate(day?: AppointmentAvailabilityDay) {
  if (!day) return "";
  return new Intl.DateTimeFormat("en-GB", { weekday: "long", month: "long", day: "numeric", year: "numeric" })
    .format(new Date(`${day.date}T12:00:00`));
}

function normalizeBookingDetails(details?: Partial<CalendlyBookingDetails>): CalendlyBookingDetails {
  const firstName = String(details?.firstName ?? "").trim();
  const lastName = String(details?.lastName ?? "").trim();
  const fullName = String(details?.fullName ?? [firstName, lastName].filter(Boolean).join(" ")).trim();

  return {
    title: String(details?.title ?? ""),
    fullName,
    firstName,
    lastName,
    email: String(details?.email ?? ""),
    phone: String(details?.phone ?? ""),
  };
}

function splitBookingName(fullName?: string | null) {
  const parts = String(fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0] ?? "", lastName: "" };
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

function getBookingPayloadName(details: CalendlyBookingDetails) {
  const splitName = splitBookingName(details.fullName);
  const firstName = details.firstName || splitName.firstName;
  const lastName = details.lastName || splitName.lastName;
  const fullName = [firstName, lastName].filter(Boolean).join(" ") || details.fullName;
  return [details.title, fullName].filter(Boolean).join(" ");
}

function hasAvailableSlots(availability?: AppointmentAvailabilityResponse | null) {
  return Boolean(availability?.days?.some((day) => day.slots?.length));
}

function formatBookingTimeRange(details?: AppointmentBookedDetails) {
  if (!details?.startTime || !details?.endTime) return "";
  const start = new Date(details.startTime);
  const end = new Date(details.endTime);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";

  const timeZone = details.timezone || "Europe/London";
  const timeFormatter = new Intl.DateTimeFormat("en-GB", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });

  return `${timeFormatter.format(start).toLowerCase()} - ${timeFormatter.format(end).toLowerCase()}`;
}

function formatBookingDate(details?: AppointmentBookedDetails) {
  if (!details?.startTime) return "";
  const start = new Date(details.startTime);
  if (Number.isNaN(start.getTime())) return "";

  return new Intl.DateTimeFormat("en-GB", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: details.timezone || "Europe/London",
  }).format(start);
}

function getHostAvatarSrc(email?: string) {
  const normalizedEmail = email?.trim().toLowerCase();
  const avatarEmailAliases: Record<string, string> = {
    "techteam+calendly@pense.co.uk": "j.allott@pense.co.uk",
  };
  const avatarEmail = normalizedEmail ? avatarEmailAliases[normalizedEmail] ?? normalizedEmail : "";
  return avatarEmail ? `/media/avatars/pense-employees/${avatarEmail}.png` : "";
}

function getInitialsFromEmail(email?: string) {
  const localPart = email?.trim().split("@")[0] ?? "";
  const initials = localPart
    .split(/[._-]+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

  return initials;
}

function getInitialsFromText(text?: string) {
  return String(text ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((part) => part[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
}

function getConfirmationHostLabel(appointmentType?: string) {
  if (appointmentType === "portal-general-appointment") {
    return "YOUR RETIREMENT SPECIALIST";
  }

  return "YOUR HOST";
}

function RequiredMarker() {
  return <span className="pa-v1-booking-required" aria-hidden="true">*</span>;
}

// Same rules as the lander's personal-details form.
const bookingEmailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
const bookingPhonePattern = /^[0-9+()\-\s]{1,15}$/;

export function getBookingFieldErrors({
  firstName,
  lastName,
  phone,
  email,
  missingQuestions,
  hasDay,
  hasSlot,
}: {
  firstName: string;
  lastName: string;
  phone: string;
  email: string;
  missingQuestions: CalendlyCustomQuestionConfig[];
  hasDay: boolean;
  hasSlot: boolean;
}): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!firstName.trim()) errors.firstName = "First name is required";
  if (!lastName.trim()) errors.lastName = "Last name is required";
  if (!phone.trim()) errors.phone = "Phone number is required";
  else if (!bookingPhonePattern.test(phone.trim())) errors.phone = "Please enter a valid phone number";
  if (!email.trim()) errors.email = "Email is required";
  else if (!bookingEmailPattern.test(email.trim())) errors.email = "Please enter a valid email address";
  missingQuestions.forEach((config) => {
    errors[`question:${config.question}`] = `${config.label ?? config.question} is required`;
  });
  if (!hasDay) errors.day = "Please select a day";
  else if (!hasSlot) errors.slot = "Please select an appointment time";
  return errors;
}

function FieldError({ message }: { message?: string }) {
  return message ? (
    <small className="pa-v1-booking-field-error" tabIndex={-1}>
      {message}
    </small>
  ) : null;
}

export function CalendlyBookingConfirmation({
  appointmentType,
  bookingDetails,
  inviteeEmail,
  ctaLabel = "START",
  ctaHref = "/register",
  onCtaClick,
  title = "To help streamline your call, please complete our questionnaire",
  warning,
  showAsFeaturedOn = true,
}: CalendlyBookingConfirmationProps) {
  const hostAvatarSrc = getHostAvatarSrc(bookingDetails?.hostEmail);
  const [hasHostAvatarError, setHasHostAvatarError] = useState(false);
  const hostLabel = getConfirmationHostLabel(appointmentType);
  const hostName = bookingDetails?.hostName?.trim();
  const bookingTitle = bookingDetails?.eventName?.trim();
  const fallbackInitials =
    getInitialsFromEmail(bookingDetails?.hostEmail) ||
    getInitialsFromText(hostName || bookingTitle || "Pense");
  const showHostAvatar = Boolean(hostAvatarSrc && !hasHostAvatarError);

  useEffect(() => {
    setHasHostAvatarError(false);
  }, [hostAvatarSrc]);

  return (
    <div className="pa-v1-booking-confirmed" aria-label="Appointment confirmation">
      <h1>You're all set!</h1>
      <article className="pa-v1-booking-host-card">
        <span>{hostLabel}</span>
        {showHostAvatar ? (
          <img
            src={hostAvatarSrc}
            alt={hostName || bookingTitle || "Appointment host"}
            onError={() => setHasHostAvatarError(true)}
          />
        ) : (
          <span className="pa-v1-booking-host-fallback" aria-hidden="true">
            {fallbackInitials}
          </span>
        )}
        <h3>{hostName || bookingTitle || "Appointment confirmed"}</h3>
        <div className="pa-v1-booking-confirmed-time">
          <i className="bi bi-calendar3" aria-hidden="true"></i>
          <p>
            <strong>{formatBookingTimeRange(bookingDetails) || "Appointment time confirmed"}</strong>
            {formatBookingDate(bookingDetails) ? <span>{formatBookingDate(bookingDetails)}</span> : null}
          </p>
        </div>
        <p className="pa-v1-booking-email-confirmation">
          Appointment confirmation has been sent to your email{inviteeEmail ? ` at ${inviteeEmail}` : ""}.
        </p>
        {warning ? <p className="pa-v1-booking-error">{warning}</p> : null}
      </article>
      <h3 className="pa-v1-booking-questionnaire-title">{title}</h3>
      {onCtaClick ? (
        <button type="button" className="pa-v1-button pa-v1-booking-start-button" onClick={onCtaClick}>{ctaLabel}</button>
      ) : (
        // In-app navigation, not a page load: the lander's tracking context and App Insights session carry on into registration.
        <Link className="pa-v1-button pa-v1-booking-start-button" to={ctaHref}>{ctaLabel}</Link>
      )}
      <p className="pa-v1-booking-home-link">
        Or <a href="/">visit our homepage</a> to find more information on getting the most from your pension
      </p>
      {showAsFeaturedOn ? <AsFeaturedOn /> : null}
    </div>
  );
}

// The details form is a single page with no buttons of its own; the widget's confirm button submits the booking.
const detailsFormSurveyProps = {
  showNavigationButtons: false,
  showQuestionNumbers: false,
  showCompletedPage: false,
  widthMode: "responsive",
  fitToContainer: true,
};

const defaultCustomQuestionConfigs: CalendlyCustomQuestionConfig[] = [
  {
    question: "Please confirm your phone number",
    position: 0,
    required: true,
    label: "Phone",
    inputType: "tel",
    autoComplete: "tel",
    bookingDetailField: "phone",
  },
];

export function CalendlyCustomWidget({
  appointmentType,
  bookingDetails,
  onBookingDetailsChange,
  onBooked,
  onBackToQuotes,
  summaryEyebrow = "Website calendars",
  summaryCopy = "Book a free appointment with one of Pense's Retirement Specialists.",
  phoneFallback,
  customQuestionConfigs = defaultCustomQuestionConfigs,
  enableSmsReminderOptIn = false,
  className = "",
  onBookedStateChange,
  onPostBookingCrmUpdate,
  confirmationCtaLabel,
  confirmationCtaHref,
  onConfirmationCtaClick,
  confirmationTitle,
  showAsFeaturedOn = true,
  detailsForm,
  stickyConfirmButton = false,
  lockInputsWhileBooking = false,
}: CalendlyCustomWidgetProps) {
  const safeBookingDetails = useMemo(() => normalizeBookingDetails(bookingDetails), [bookingDetails]);
  const storedBooking = useMemo(() => readStoredAppointmentBooking(appointmentType), [appointmentType]);
  const [month, setMonth] = useState(getAppointmentMonth);
  const [availability, setAvailability] = useState<AppointmentAvailabilityResponse | null>(null);
  const [availabilityCache, setAvailabilityCache] = useState<Record<string, AppointmentAvailabilityResponse>>({});
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedSlot, setSelectedSlot] = useState("");
  const [isLoadingAvailability, setIsLoadingAvailability] = useState(false);
  const [isSearchingMonth, setIsSearchingMonth] = useState(false);
  const [isRefreshingDay, setIsRefreshingDay] = useState(false);
  const [isBooking, setIsBooking] = useState(false);
  const [error, setError] = useState("");
  const [isBooked, setIsBooked] = useState(Boolean(storedBooking?.bookingDetails));
  const [bookedDetails, setBookedDetails] = useState<AppointmentBookedDetails | undefined>(storedBooking?.bookingDetails);
  const [bookedInviteeEmail, setBookedInviteeEmail] = useState(storedBooking?.inviteeEmail);
  const [postBookingWarning, setPostBookingWarning] = useState("");
  const [hasReachedFutureLimit, setHasReachedFutureLimit] = useState(false);
  const [noAvailabilityMonth, setNoAvailabilityMonth] = useState("");
  const [customQuestionAnswers, setCustomQuestionAnswers] = useState<Record<string, string>>({});
  const [smsReminderOptIn, setSmsReminderOptIn] = useState(() => Boolean(enableSmsReminderOptIn && safeBookingDetails.phone.trim()));
  const apiRequestLockedRef = useRef(false);

  const availableByDate = useMemo(() => {
    return new Map((availability?.days ?? []).map((day) => [day.date, day]));
  }, [availability]);
  const selectedDay = selectedDate ? availableByDate.get(selectedDate) : undefined;
  const getCustomQuestionAnswer = useCallback((config: CalendlyCustomQuestionConfig) => {
    const detailAnswer = config.bookingDetailField ? safeBookingDetails[config.bookingDetailField] : undefined;
    return String(detailAnswer ?? customQuestionAnswers[config.question] ?? config.getDefaultAnswer?.(safeBookingDetails) ?? "").trim();
  }, [safeBookingDetails, customQuestionAnswers]);
  const customQuestionsToComplete = customQuestionConfigs.filter(
    (config) => config.required && !config.bookingDetailField && !getCustomQuestionAnswer(config)
  );
  const smsReminderNumber = smsReminderOptIn ? toE164PhoneFormatter(safeBookingDetails.phone) : undefined;
  const hasInvalidSmsReminderPhone = Boolean(smsReminderOptIn && safeBookingDetails.phone && !smsReminderNumber);
  const fallbackNamePartsForValidation = splitBookingName(safeBookingDetails.fullName);
  const firstNameValue = safeBookingDetails.firstName || fallbackNamePartsForValidation.firstName;
  const lastNameValue = safeBookingDetails.lastName || fallbackNamePartsForValidation.lastName;
  const allFieldErrors = getBookingFieldErrors({
    firstName: firstNameValue,
    lastName: lastNameValue,
    phone: safeBookingDetails.phone,
    email: safeBookingDetails.email,
    missingQuestions: customQuestionsToComplete,
    hasDay: Boolean(selectedDate),
    hasSlot: Boolean(selectedSlot),
  });
  // A details form validates its own fields; only the calendar selection is checked here.
  const fieldErrors = detailsForm
    ? Object.fromEntries(Object.entries(allFieldErrors).filter(([key]) => !contactFieldErrorKeys.includes(key)))
    : allFieldErrors;
  const detailsFormRef = useRef<SurveyJSFormRef>(null);
  const confirmButtonRef = useRef<HTMLDivElement>(null);
  const hasFieldErrors = Object.keys(fieldErrors).length > 0;
  const [showValidation, setShowValidation] = useState(false);
  const visibleErrors = showValidation ? fieldErrors : {};
  const cardRef = useRef<HTMLDivElement>(null);
  const isConfirmBusy = isLoadingAvailability || isRefreshingDay || isBooking;
  const areInputsLocked = lockInputsWhileBooking && isBooking;
  // The pinned button takes the details form's width.
  useStickyBottomBar(confirmButtonRef, {
    enabled: stickyConfirmButton && !isBooked,
    barSelector: ".lv2-sticky-bar",
    widthSelector: ".pa-v1-booking-details-form .sd-page",
  });

  const loadAvailability = useCallback(async (targetMonth: string, force = false, apply = true) => {
    if (!force && availabilityCache[targetMonth]) {
      const cached = availabilityCache[targetMonth];
      if (apply) {
        setAvailability(cached);
        setMonth(targetMonth);
      }
      return cached;
    }

    const calendarConfig = getCalendarConfig(appointmentType);
    const query = new URLSearchParams({ appointmentType, month: targetMonth });
    if (calendarConfig.eventTypeId) {
      query.set("provider", calendarConfig.provider);
      query.set("eventTypeId", calendarConfig.eventTypeId);
    }

    const response = await fetch(`${APIURL}/appointments/availability?${query.toString()}`);
    const payload = await response.json();
    if (!response.ok) {
      throw new Error(payload?.message ?? "Appointment availability failed.");
    }

    setAvailabilityCache((current) => ({ ...current, [targetMonth]: payload }));
    if (apply) {
      setAvailability(payload);
      setMonth(targetMonth);
    }
    return payload as AppointmentAvailabilityResponse;
  }, [appointmentType, availabilityCache]);

  function tryStartApiRequest() {
    if (apiRequestLockedRef.current || isLoadingAvailability || isSearchingMonth || isRefreshingDay || isBooking) {
      return false;
    }

    apiRequestLockedRef.current = true;
    return true;
  }

  function finishApiRequest() {
    window.setTimeout(() => {
      apiRequestLockedRef.current = false;
    }, 250);
  }

  useEffect(() => {
    let cancelled = false;

    async function loadInitialAvailability() {
      if (isBooked) {
        return;
      }

      setIsLoadingAvailability(true);
      setError("");
      try {
        await loadAvailability(month);
      } catch (loadError: any) {
        if (!cancelled) {
          setAvailability(null);
          setError(loadError?.message ?? "Appointment availability failed. Please call us instead.");
        }
      } finally {
        if (!cancelled) setIsLoadingAvailability(false);
      }
    }

    loadInitialAvailability();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentType, isBooked]);

  useEffect(() => {
    onBookedStateChange?.(isBooked);
  }, [isBooked, onBookedStateChange]);

  useEffect(() => {
    if (!noAvailabilityMonth) return;
    const timeout = window.setTimeout(() => setNoAvailabilityMonth(""), 5000);
    return () => window.clearTimeout(timeout);
  }, [noAvailabilityMonth]);

  async function moveToAvailableMonth(delta: number, fromMonth = month) {
    if (!tryStartApiRequest()) return;

    setIsSearchingMonth(true);
    setIsLoadingAvailability(true);
    setError("");
    setNoAvailabilityMonth("");
    setHasReachedFutureLimit(false);
    setSelectedDate("");
    setSelectedSlot("");

    try {
      let candidateMonth = fromMonth;
      let lastUnavailableMonth = "";
      for (let i = 0; i < 12; i++) {
        candidateMonth = shiftAppointmentMonth(candidateMonth, delta);
        if (delta < 0 && candidateMonth < getAppointmentMonth()) break;

        const candidateAvailability = await loadAvailability(candidateMonth, false, false);
        if (hasAvailableSlots(candidateAvailability)) {
          setNoAvailabilityMonth("");
          setAvailability(candidateAvailability);
          setMonth(candidateMonth);
          return;
        }

        if (delta > 0) {
          lastUnavailableMonth = candidateMonth;
        }
      }

      if (delta > 0) {
        setHasReachedFutureLimit(true);
        if (lastUnavailableMonth) {
          setNoAvailabilityMonth(lastUnavailableMonth);
        }
      }
      setError(delta > 0 ? "" : "No earlier available appointment dates were found.");
    } catch (loadError: any) {
      setError(loadError?.message ?? "Appointment availability failed. Please call us instead.");
    } finally {
      setIsSearchingMonth(false);
      setIsLoadingAvailability(false);
      finishApiRequest();
    }
  }

  async function selectDay(date: string) {
    if (!tryStartApiRequest()) return;

    setIsRefreshingDay(true);
    setIsLoadingAvailability(true);
    setError("");
    setSelectedSlot("");
    try {
      const refreshed = await loadAvailability(month, true);
      const refreshedDay = refreshed.days.find((day) => day.date === date);
      if (!refreshedDay?.slots?.length) {
        setSelectedDate("");
        setSelectedSlot("");
        setError("That day is no longer available. Please choose another day.");
        return;
      }

      setSelectedDate(date);
      setSelectedSlot("");
    } catch (loadError: any) {
      setError(loadError?.message ?? "Appointment availability failed. Please call us instead.");
    } finally {
      setIsRefreshingDay(false);
      setIsLoadingAvailability(false);
      finishApiRequest();
    }
  }

  // The button is always clickable: a click first checks the details and the calendar selection, even while
  // times are loading, and only then waits for any request in flight.
  function readDetailsForm() {
    const formData = unflattenData(detailsFormRef.current?.getData() ?? {}) as Record<string, any>;
    const fieldMap = { ...defaultDetailsFormFieldMap, ...detailsForm?.fieldMap };
    const valueOf = (field: string) =>
      String(field.split(".").reduce<any>((value, key) => value?.[key], formData) ?? "").trim();
    const details = normalizeBookingDetails({
      title: valueOf(fieldMap.title),
      firstName: valueOf(fieldMap.firstName),
      lastName: valueOf(fieldMap.lastName),
      email: valueOf(fieldMap.email),
      phone: valueOf(fieldMap.phone),
    });
    return { formData, details };
  }

  async function confirmBooking() {
    const isDetailsFormValid = detailsForm ? detailsFormRef.current?.validate() ?? false : true;
    const fromForm = detailsForm ? readDetailsForm() : undefined;
    const details = fromForm?.details ?? safeBookingDetails;
    const reminderNumber = smsReminderOptIn ? toE164PhoneFormatter(details.phone) : undefined;
    const hasInvalidReminderPhone = Boolean(smsReminderOptIn && details.phone && !reminderNumber);

    if (fromForm) {
      onBookingDetailsChange(details);
    }

    if (hasFieldErrors || hasInvalidReminderPhone || !isDetailsFormValid) {
      setShowValidation(true);
      // Take the visitor to the first field that needs fixing.
      window.requestAnimationFrame(() =>
        cardRef.current
          ?.querySelector<HTMLElement>("[aria-invalid='true'], .pa-v1-booking-field-error, .pa-v1-booking-sms-error, .sd-question--error")
          ?.focus()
      );
      return;
    }

    if (!tryStartApiRequest()) return;

    const fallbackNameParts = splitBookingName(details.fullName);
    const firstNameForBooking = details.firstName || fallbackNameParts.firstName;
    const lastNameForBooking = details.lastName || fallbackNameParts.lastName;
    const customQuestions = customQuestionConfigs.map((config) => ({
      question: config.question,
      answer:
        fromForm && config.bookingDetailField
          ? String(details[config.bookingDetailField] ?? "").trim()
          : getCustomQuestionAnswer(config),
      position: config.position,
    }));
    const selectedSlotDetails = selectedDay?.slots.find((slot) => slot.startTime === selectedSlot);
    const fallbackBookingDetails: AppointmentBookedDetails = {
      startTime: selectedSlot,
      endTime: selectedSlotDetails?.endTime,
      timezone: availability?.timezone ?? "Europe/London",
      eventName: availability?.label,
    };

    setIsBooking(true);
    setError("");
    setPostBookingWarning("");

    try {
      const response = await fetch(`${APIURL}/appointments/book`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          appointmentType,
          calendarConfig: getCalendarConfig(appointmentType),
          startTime: selectedSlot,
          timezone: availability?.timezone ?? "Europe/London",
          title: details.title,
          name: getBookingPayloadName(details),
          firstName: firstNameForBooking,
          lastName: lastNameForBooking,
          email: details.email,
          phone: details.phone,
          ...(reminderNumber ? { textReminderNumber: reminderNumber } : {}),
          customQuestions: customQuestions.filter((item) => item.question && item.answer),
        }),
      });
      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload?.message ?? "Appointment booking failed.");
      }

      const payloadWithBookingDetails = {
        ...payload,
        bookingDetails: payload?.bookingDetails ?? fallbackBookingDetails,
      };
      try {
        await onPostBookingCrmUpdate?.(payloadWithBookingDetails, details, fromForm?.formData);
      } catch (crmUpdateError) {
        console.error("Post-booking CRM update failed", crmUpdateError);
        setPostBookingWarning("Your appointment was booked, but we could not save all details. Please call us if you need to change anything.");
      }
      setBookedDetails(payloadWithBookingDetails.bookingDetails);
      setBookedInviteeEmail(details.email);
      setIsBooked(true);
      onBookedStateChange?.(true);
      writeStoredAppointmentBooking({
        appointmentType,
        bookingId: payload?.eventUri ?? payload?.inviteeUri,
        bookingDetails: payloadWithBookingDetails.bookingDetails,
        inviteeEmail: details.email,
        savedAt: new Date().toISOString(),
      });
      onBooked(payloadWithBookingDetails);
    } catch (bookingError: any) {
      setError(bookingError?.message ?? "We could not confirm that time. Please choose another time or call us.");
    } finally {
      setIsBooking(false);
      finishApiRequest();
    }
  }

  const confirmButton = (
    <button type="button" className="pa-v1-button" aria-busy={isConfirmBusy} onClick={confirmBooking}>
      {isBooking ? (
        <>
          CONFIRMING...
          <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
        </>
      ) : (
        "CONFIRM APPOINTMENT TIME"
      )}
    </button>
  );

  const calendarDays = getAppointmentCalendarDays(month);
  const currentMonth = getAppointmentMonth();
  const isMonthLoading = isLoadingAvailability || isSearchingMonth;
  const hasCurrentMonthAvailability = hasAvailableSlots(availability);
  const isInitialAvailabilityPending = !availability && !error;
  const bookingSummaryTitle = isInitialAvailabilityPending
    ? "Loading calendar..."
    : availability?.label ?? "Appointment calendar unavailable";

  return (
    <div ref={cardRef} className={`pa-v1-custom-booking-card ${isBooked ? "pa-v1-custom-booking-card-confirmed" : ""} ${className}`.trim()}>
      {isBooked ? (
        <CalendlyBookingConfirmation
          appointmentType={appointmentType}
          bookingDetails={bookedDetails}
          inviteeEmail={bookedInviteeEmail || safeBookingDetails.email}
          ctaLabel={confirmationCtaLabel}
          ctaHref={confirmationCtaHref}
          onCtaClick={onConfirmationCtaClick}
          title={confirmationTitle}
          warning={postBookingWarning}
          showAsFeaturedOn={showAsFeaturedOn}
        />
      ) : (
        <>
          <div className="pa-v1-booking-summary">
            <h2>{bookingSummaryTitle}</h2>
            {availability ? <span>{availability.durationMinutes} min</span> : null}
            <p>{summaryCopy}</p>
          </div>
          <hr />
          {/* A disabled fieldset disables every control inside it, the details form's SurveyJS inputs included. */}
          <fieldset className="pa-v1-booking-lock" disabled={areInputsLocked}>
          <div className="pa-v1-booking-details" aria-label="Confirm your details">
            <h3>Confirm your details</h3>
            {detailsForm ? (
              <div className="pa-v1-booking-details-form">
                <Suspense fallback={null}>
                  <SurveyJSForm
                    ref={detailsFormRef}
                    surveyFormId={detailsForm.surveyFormId}
                    pageSchemas={[detailsForm.schemaPage]}
                    surveyProps={detailsFormSurveyProps}
                    surveyTheme={lv2Theme}
                    wrapperFormCssClass="lv2-survey pa-v1-booking-survey"
                    clearDraftOnComplete={false}
                    keepFormVisibleOnComplete
                    suppressLoader
                    disableTokenPrefill
                    onFirstInteraction={detailsForm.onFirstInteraction}
                    onComplete={() => undefined}
                  />
                </Suspense>
                {enableSmsReminderOptIn ? (
                  <label className="pa-v1-booking-sms-opt-in">
                    <input
                      type="checkbox"
                      checked={smsReminderOptIn}
                      onChange={(event) => setSmsReminderOptIn(event.target.checked)}
                    />
                    <span>
                      <strong>Send me SMS appointment reminders at this number.</strong>
                      <small>Message and data rates may apply. You can opt out at any time.</small>
                    </span>
                  </label>
                ) : null}
              </div>
            ) : (
            <div>
              <label>
                <span>Title</span>
                <select
                  value={safeBookingDetails.title ?? ""}
                  onChange={(event) => onBookingDetailsChange({ ...safeBookingDetails, title: event.target.value })}
                >
                  <option value="">Select</option>
                  <option value="Mr">Mr</option>
                  <option value="Mrs">Mrs</option>
                  <option value="Miss">Miss</option>
                  <option value="Ms">Ms</option>
                  <option value="Dr">Dr</option>
                </select>
              </label>
              <label>
                <span>First name <RequiredMarker /></span>
                <input
                  type="text"
                  value={firstNameValue}
                  required
                  autoComplete="given-name"
                  aria-invalid={Boolean(visibleErrors.firstName)}
                  onChange={(event) => {
                    const firstName = event.target.value;
                    const lastName = safeBookingDetails.lastName || splitBookingName(safeBookingDetails.fullName).lastName;
                    onBookingDetailsChange({
                      ...safeBookingDetails,
                      firstName,
                      lastName,
                      fullName: [firstName, lastName].filter(Boolean).join(" "),
                    });
                  }}
                />
                <FieldError message={visibleErrors.firstName} />
              </label>
              <label>
                <span>Last name <RequiredMarker /></span>
                <input
                  type="text"
                  value={lastNameValue}
                  required
                  autoComplete="family-name"
                  aria-invalid={Boolean(visibleErrors.lastName)}
                  onChange={(event) => {
                    const lastName = event.target.value;
                    const firstName = safeBookingDetails.firstName || splitBookingName(safeBookingDetails.fullName).firstName;
                    onBookingDetailsChange({
                      ...safeBookingDetails,
                      firstName,
                      lastName,
                      fullName: [firstName, lastName].filter(Boolean).join(" "),
                    });
                  }}
                />
                <FieldError message={visibleErrors.lastName} />
              </label>
              <label>
                <span>Phone <RequiredMarker /></span>
                <input
                  type="tel"
                  value={safeBookingDetails.phone}
                  required
                  autoComplete="tel"
                  aria-invalid={Boolean(visibleErrors.phone)}
                  onChange={(event) => {
                    const nextPhone = event.target.value;
                    onBookingDetailsChange({ ...safeBookingDetails, phone: nextPhone });
                    if (!nextPhone.trim()) {
                      setSmsReminderOptIn(false);
                    }
                  }}
                />
                <FieldError message={visibleErrors.phone} />
              </label>
              <label>
                <span>Email <RequiredMarker /></span>
                <input
                  type="email"
                  value={safeBookingDetails.email}
                  required
                  autoComplete="email"
                  aria-invalid={Boolean(visibleErrors.email)}
                  onChange={(event) => onBookingDetailsChange({ ...safeBookingDetails, email: event.target.value })}
                />
                <FieldError message={visibleErrors.email} />
              </label>
              {customQuestionConfigs
                .filter((config) => !config.bookingDetailField)
                .map((config) => (
                  <label key={config.question}>
                    <span>{config.label ?? config.question} {config.required ? <RequiredMarker /> : null}</span>
                    <input
                      type={config.inputType ?? "text"}
                      value={customQuestionAnswers[config.question] ?? config.getDefaultAnswer?.(safeBookingDetails) ?? ""}
                      required={config.required}
                      autoComplete={config.autoComplete}
                      aria-invalid={Boolean(visibleErrors[`question:${config.question}`])}
                      onChange={(event) => setCustomQuestionAnswers((current) => ({
                        ...current,
                        [config.question]: event.target.value,
                      }))}
                    />
                    <FieldError message={visibleErrors[`question:${config.question}`]} />
                  </label>
                ))}
              {enableSmsReminderOptIn ? (
                <label className="pa-v1-booking-sms-opt-in">
                  <input
                    type="checkbox"
                    checked={smsReminderOptIn}
                    disabled={!safeBookingDetails.phone.trim()}
                    onChange={(event) => setSmsReminderOptIn(event.target.checked)}
                  />
                  <span>
                    <strong>Send me SMS appointment reminders at this number.</strong>
                    <small>Message and data rates may apply. You can opt out at any time.</small>
                  </span>
                </label>
              ) : null}
            </div>
            )}
            {hasInvalidSmsReminderPhone && !detailsForm ? (
              <p className="pa-v1-booking-error pa-v1-booking-sms-error" tabIndex={-1}>Please enter a valid phone number for SMS reminders, or untick SMS reminders to continue.</p>
            ) : null}
          </div>
          </fieldset>
          <hr />
          <div className="pa-v1-booking-picker">
            <div className="pa-v1-booking-calendar">
              <h3>Select a Day</h3>
              <div className="pa-v1-booking-month">
                <button type="button" aria-label="Previous available month" disabled={isMonthLoading || areInputsLocked || month <= currentMonth} onClick={() => moveToAvailableMonth(-1)}>
                  <i className="bi bi-chevron-left" aria-hidden="true"></i>
                </button>
                <strong>{formatAppointmentMonth(month)}</strong>
                <button type="button" aria-label="Next available month" disabled={isMonthLoading || areInputsLocked || hasReachedFutureLimit} onClick={() => moveToAvailableMonth(1)}>
                  <i className="bi bi-chevron-right" aria-hidden="true"></i>
                </button>
              </div>
              {noAvailabilityMonth ? (
                <p className="pa-v1-booking-month-notice">No availability in {formatAppointmentMonth(noAvailabilityMonth)}</p>
              ) : null}
              {isMonthLoading ? <span className="pa-v1-booking-loading-bar" aria-hidden="true"></span> : null}
              {!isMonthLoading && availability && !hasCurrentMonthAvailability && !hasReachedFutureLimit ? (
                <p className="pa-v1-booking-month-empty">All appointments are booked this month. Try another month.</p>
              ) : null}
              <div className="pa-v1-booking-weekdays" aria-hidden="true">
                <span>Mon</span><span>Tue</span><span>Wed</span><span>Thu</span><span>Fri</span><span>Sat</span><span>Sun</span>
              </div>
              <div className="pa-v1-booking-days" role="radiogroup" aria-label="Select a day" aria-busy={isMonthLoading}>
                {calendarDays.map((calendarDay) => {
                  if (calendarDay.isBlank) {
                    return <span key={calendarDay.key} aria-hidden="true"></span>;
                  }
                  const availableDay = availableByDate.get(calendarDay.date);
                  const isAvailable = Boolean(availableDay?.slots?.length);
                  const isSelected = selectedDate === calendarDay.date;
                  return (
                    <button
                      key={calendarDay.key}
                      type="button"
                      disabled={!isAvailable || isMonthLoading || areInputsLocked}
                      className={isSelected ? "pa-v1-booking-day-selected" : ""}
                      aria-checked={isSelected}
                      role="radio"
                      onClick={() => selectDay(calendarDay.date)}
                    >
                      {calendarDay.day}
                    </button>
                  );
                })}
              </div>
              <p className="pa-v1-booking-timezone">UK, Ireland, Lisbon Time</p>
            </div>

            <div className="pa-v1-booking-times">
              <h3>{selectedDay ? formatSelectedAppointmentDate(selectedDay) : "Select a Time"}</h3>
              {selectedDay ? (
                <div role="radiogroup" aria-label="Select a time">
                  {selectedDay.slots.map((slot) => (
                    <button
                      key={slot.startTime}
                      type="button"
                      disabled={isRefreshingDay || isLoadingAvailability || isBooking}
                      className={selectedSlot === slot.startTime ? "pa-v1-booking-time-selected" : ""}
                      role="radio"
                      aria-checked={selectedSlot === slot.startTime}
                      onClick={() => setSelectedSlot(slot.startTime)}
                    >
                      {slot.label}
                    </button>
                  ))}
                </div>
              ) : (
                <p>Choose an available day to see appointment times.</p>
              )}
              <FieldError message={visibleErrors.day ?? visibleErrors.slot} />
              {stickyConfirmButton ? (
                <div ref={confirmButtonRef} className="lv2-sticky">
                  <div className="lv2-sticky-bar">{confirmButton}</div>
                </div>
              ) : (
                confirmButton
              )}
            </div>
          </div>
          {isMonthLoading ? <p className="pa-v1-booking-status">Loading available times...</p> : null}
          {error ? <p className="pa-v1-booking-error">{error}</p> : null}
          {phoneFallback}
        </>
      )}
    </div>
  );
}
